<template>

    <v-radio-group v-model="crtSelectedValue"
                   :hide-details="hideDetails"
                   class="base-radio-group">
        <v-radio v-for="(item, index) in itemList"
                 :key="index"
                 :label="(isListOfObjects ? item[labelKey] : item)"
                 :value="(isListOfObjects ? item[valueKey] : item)"
                 color="primary">
        </v-radio>
    </v-radio-group>

</template>

<script>

/**
 * This component is a wrapper for vuetify's radio group.
 * It helps us with the custom icons for checked radio buttons.
 *
 * PROPS:
 * - `itemList`: The list of items from which the user can select one item.
 *          - If `itemList` is a list of strings (each item being a string), that string will be used as value and as label too.
 *          - If `itemList` is a list of objects (each item being an object), you MUST provide `valueKey` and `labelKey` props.
 * - `selectedValue` (optional): The value of the initially selected item.
 *          - If the type of `itemList` is String[], that item (string) will be used as value.
 *          - If the type of `itemList` is Object[], `valueKey` prop will be used to find out which key of the item object
 *              is the one that keeps the value of selection.
 * - `valueKey` (conditioned optional): Required if the type of `itemList` is Object[]. It tells which key of the objects in `itemList` keeps radio buttons` values.
 *          - `valueKey` is not needed and it will be ignored if the type of `itemList` is String[].
 * - `labelKey` (conditioned optional): Required if the type of `itemList` is Object[]. It tells which key of the objects in `itemList` keeps radio buttons` labels.
 *          - `labelKey` is not needed and it will be ignored if the type of `itemList` is String[].
 *
 * @emits `onChangeValue` custom event having as payload an object with `newValue` property. Ex. `{newValue: 123}`
 */

import Vue from "vue";
import {mapGetters} from "vuex";


export default {
    name: "BaseRadioGroup",

    props: {
        // Required props:
        itemList: {
            type: Array,
            required: true
        },
        selectedValue: {
            type: [String, Number],
        },
        // Props required if "itemList" is a list of objects, ignored otherwise:
        valueKey: {
            type: String
        },
        labelKey: {
            type: String
        },

        // Optional props:
        hideDetails: {
            type: [Boolean, String]
        }
    },

    computed: {
        ...mapGetters({
            getAppPropertyValue: 'TranslationStore/getAppPropertyValue',
        }),

        crtSelectedValue: {
            get() {
                return this.selectedValue;
            },
            set(newValue) {
                this.$emit('onChangeValue', {newValue: newValue});
            },
        },

        /**
         * Checks if itemList prop contains an array of objects or an array of something else (ex. array of strings).
         * @return {boolean}
         */
        isListOfObjects() {
            if (this.itemList.length === 0) {
                return false;
            }

            let firstItem = this.itemList[0];

            return (typeof firstItem === "object"
                && typeof firstItem[this.valueKey] !== "undefined"
                && typeof firstItem[this.labelKey] !== "undefined") ? true : false;
        }
    },

    mounted() {
        this.$nextTick(() => {
            this.fixCheckedRadioIcon();
        });
    },

    watch: {
        crtSelectedValue () {
            this.$nextTick(() => {
                this.fixCheckedRadioIcon();
            });
        },

    },

    methods: {

        /**
         * Make selected radio button (icon) to look fine by replacing the vuetify icon with a custom SVG icon.
         * @return {Void}
         */
        fixCheckedRadioIcon() {
            // Inject icon.
            let radioElemIcons = document.querySelectorAll('.base-radio-group .input-group--selection-controls.radio .input-group__input i.icon.icon--selection-control.icon--radio');
            radioElemIcons.forEach((radioIcon) => {
                radioIcon.innerHTML = Vue.RadioButton.RadioButtonSvg();
            });
        }
    }

}
</script>



// WEBPACK FOOTER //
// src/common/components/shared/BaseRadioGroup.vue